import { SpanStatusCode } from '@opentelemetry/api';
import type { ReadableSpan, SpanProcessor } from '@opentelemetry/sdk-trace-base';

import { BatchedIngestClient } from './batched-ingest.client';
import {
  APP_BOOTSTRAP_PAGE_NAME,
  SPA_INGEST_SCHEMA_VERSION,
  SPA_OTEL_ATTR,
  SPA_PERF_SPAN_PREFIX,
  SpaIngestBatch,
  SpaIngestEmitter,
  SpaIngestEventKind,
  SpaPageLoadIngestEvent,
  SpaPerformanceEventType,
} from './metrics.types';
import { randomBase36Segment } from './secure-random.util';

export interface MetricsSpanProcessorOptions {
  client: BatchedIngestClient;
  emitter: SpaIngestEmitter;
  /** Maximum events buffered before a flush is triggered. */
  flushSize: number;
  /** Periodic flush interval (milliseconds). */
  flushIntervalMs: number;
  /**
   * Resolves the tenantId at the moment a span is converted to an event.
   * In production this reads from `sessionStorage`. Injected so we can
   * test the processor without touching browser state.
   */
  tenantIdProvider: () => string | null;
  /**
   * Optional `setTimeout` / `clearTimeout` injection for testability. Defaults
   * to the global timer functions.
   */
  timers?: {
    setTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
    clearTimeout: (handle: ReturnType<typeof setTimeout>) => void;
  };
  /** Optional logger; defaults to silent. */
  onWarn?: (msg: string, ctx?: Record<string, unknown>) => void;
}

/**
 * Custom OTEL `SpanProcessor` that:
 *
 * 1. Filters spans to those starting with `metrics.performance.` (Phase 2D
 *    only emits PAGE_LOAD; later phases may add `metrics.performance.query.*`).
 * 2. Drops failed/cancelled spans (per Choice 5: success-only).
 * 3. Converts each kept span into an `SpaPageLoadIngestEvent`.
 * 4. Buffers events; flushes via `BatchedIngestClient` on size or interval.
 * 5. `forceFlush()` is wired up so OTEL shutdown / unload hooks drain pending
 *    events. The buffer is also drained synchronously when `forceFlush` is
 *    called from `beforeunload` (`fetch keepalive: true` lets the request
 *    survive the tab close).
 *
 * Safety: this processor never throws; failures are logged via `onWarn`.
 */
export class MetricsSpanProcessor implements SpanProcessor {
  private readonly buffer: SpaPageLoadIngestEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private shutdownInProgress = false;
  private readonly setTimeoutFn: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  private readonly clearTimeoutFn: (handle: ReturnType<typeof setTimeout>) => void;

  constructor(private readonly options: MetricsSpanProcessorOptions) {
    this.setTimeoutFn =
      options.timers?.setTimeout ?? ((fn, ms) => setTimeout(fn, ms) as unknown as ReturnType<typeof setTimeout>);
    this.clearTimeoutFn =
      options.timers?.clearTimeout ?? ((h) => clearTimeout(h as unknown as Parameters<typeof clearTimeout>[0]));
  }

  public onStart(): void {
    // No-op. We only need terminal-side processing.
  }

  public onEnd(span: ReadableSpan): void {
    if (this.shutdownInProgress) {
      return;
    }
    if (!span.name.startsWith(SPA_PERF_SPAN_PREFIX)) {
      return;
    }
    if (span.status?.code === SpanStatusCode.ERROR) {
      return;
    }
    const event = this.toEvent(span);
    if (!event) {
      return;
    }
    this.buffer.push(event);

    if (this.buffer.length >= this.options.flushSize) {
      void this.flushNow();
      return;
    }
    this.scheduleTimer();
  }

  public async forceFlush(): Promise<void> {
    await this.flushNow();
  }

  public async shutdown(): Promise<void> {
    this.shutdownInProgress = true;
    if (this.timer) {
      this.clearTimeoutFn(this.timer);
      this.timer = null;
    }
    await this.flushNow();
  }

  private scheduleTimer(): void {
    if (this.timer) {
      return;
    }
    this.timer = this.setTimeoutFn(() => {
      this.timer = null;
      void this.flushNow();
    }, this.options.flushIntervalMs);
  }

  private async flushNow(): Promise<void> {
    if (this.timer) {
      this.clearTimeoutFn(this.timer);
      this.timer = null;
    }
    if (this.buffer.length === 0) {
      return;
    }
    const events = this.buffer.splice(0, this.buffer.length);
    const batch: SpaIngestBatch = {
      schemaVersion: SPA_INGEST_SCHEMA_VERSION,
      emitter: this.options.emitter,
      events,
    };
    try {
      const outcome = await this.options.client.flush(batch);
      if (outcome.kind === 'transient' && outcome.attemptsExhausted) {
        this.options.onWarn?.('metrics.flush.transient.exhausted', { events: events.length, status: outcome.status });
      } else if (outcome.kind === 'permanent') {
        this.options.onWarn?.('metrics.flush.permanent', {
          events: events.length,
          status: outcome.status,
        });
      }
    } catch (err) {
      this.options.onWarn?.('metrics.flush.unexpected_error', {
        events: events.length,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private toEvent(span: ReadableSpan): SpaPageLoadIngestEvent | null {
    const attrs = span.attributes;
    const tenantId = (attrs[SPA_OTEL_ATTR.TENANT_ID] as string | undefined) ?? this.options.tenantIdProvider() ?? null;
    if (!tenantId) {
      return null;
    }

    const pageName = (attrs[SPA_OTEL_ATTR.PAGE_NAME] as string | undefined) ?? APP_BOOTSTRAP_PAGE_NAME;

    const durationSeconds = this.computeDurationSeconds(span, attrs);
    if (durationSeconds === null || durationSeconds < 0) {
      return null;
    }

    const eventType =
      (attrs[SPA_OTEL_ATTR.EVENT_TYPE] as SpaPerformanceEventType | undefined) ?? SpaPerformanceEventType.PAGE_LOAD;
    if (eventType !== SpaPerformanceEventType.PAGE_LOAD) {
      // Phase 2D only ships PAGE_LOAD. Other types would need a different
      // backend kind (e.g. QUERY) which the SPA endpoint rejects today.
      return null;
    }

    const sourceEventId = (attrs[SPA_OTEL_ATTR.SOURCE_EVENT_ID] as string | undefined) ?? this.generateId();

    const sampleRatio = (attrs[SPA_OTEL_ATTR.SAMPLE_RATIO] as number | undefined) ?? undefined;

    const occurredAt = new Date(this.spanStartMs(span)).toISOString();

    return {
      id: sourceEventId,
      tenantId,
      occurredAt,
      sampleRatio,
      kind: SpaIngestEventKind.PERFORMANCE,
      eventType: SpaPerformanceEventType.PAGE_LOAD,
      pageName,
      durationSeconds,
    };
  }

  private computeDurationSeconds(span: ReadableSpan, attrs: ReadableSpan['attributes']): number | null {
    const explicit = attrs[SPA_OTEL_ATTR.DURATION_SECONDS];
    if (typeof explicit === 'number' && Number.isFinite(explicit)) {
      return explicit;
    }
    // Fallback: derive from span timing. ReadableSpan exposes hrTime tuples
    // [seconds, nanos]; OTEL SDK also exposes `duration` in the same shape.
    const duration = (span as unknown as { duration?: [number, number] }).duration;
    if (Array.isArray(duration) && duration.length === 2) {
      return duration[0] + duration[1] / 1e9;
    }
    return null;
  }

  private spanStartMs(span: ReadableSpan): number {
    const startTime = (span as unknown as { startTime?: [number, number] }).startTime;
    if (Array.isArray(startTime) && startTime.length === 2) {
      return startTime[0] * 1000 + startTime[1] / 1e6;
    }
    return Date.now();
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `evt-${Date.now()}-${randomBase36Segment(8)}`;
  }
}
